import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { Schedule } from '@robocorp/robocloud-scheduler-interface';

import { locales } from '~/constants';
import { TimestampFormatOptions } from '~/types';
import { getTimezoneValue, getZonedDate, LOCAL_TZ_VALUE } from './timezoneHelpers';

export const dayInMilliseconds = 60 * 60 * 24 * 1000;
/**
 * Returns earliest date out of an array of date strings
 * @param dates
 */
export const getEarliestDate = (dates: string[]) =>
  dates.reduce((pre, cur) => (Date.parse(pre) > Date.parse(cur) ? cur : pre), dates[0]);

export const dateObjectToDateString = (date: Date): string => date.toISOString().split('T')[0];

export const getMonthDayString = (date: Date): string => {
  return `${date.toLocaleString('en-EN', {
    month: 'short',
  })} ${date.getDate()}`;
};

export const dateToMidnight = (date: Date): Date => {
  // Prevents mutating the date that is passed in as argument
  const day = new Date(date);
  day.setUTCHours(0, 0, 0, 0);
  return day;
};

export const getDates = (count: number, endDate: Date): Date[] => {
  const dates: Date[] = [];
  for (let i = 0; i < count; i += 1) {
    dates.unshift(dateToMidnight(new Date(endDate.getTime() - dayInMilliseconds * i)));
  }
  return dates;
};

export const calculateTimespan = (date1: Date, date2: Date) => {
  return Math.round(Math.abs((date2.valueOf() - date1.valueOf()) / dayInMilliseconds));
};

export const getDayRange = (startDate: Date, endDate: Date): string[] => {
  if (startDate.getTime() > endDate.getTime()) return [];

  let start = startDate;
  const range = [startDate.toISOString().split('T')[0]];
  do {
    start = addDays(start, 1);
    range.push(start.toISOString().split('T')[0]);
  } while (start.getTime() < endDate.getTime());

  return range;
};

export const getDateRangeLabel = (startDate: Date, endDate: Date): string => {
  const start = format(startDate, 'MMM d');
  const end = format(endDate, 'MMM d, y');
  return `${start} - ${end}`;
};

export const getDateWithoutSeconds = (date: Date) => {
  const next = new Date(date.getTime());
  next.setMilliseconds(0);
  next.setSeconds(0);
  return next;
};

export const getStartOfDayInTimezone = (date: Date, timezone: string | 'local') => {
  if (timezone === 'local') return startOfDay(date);
  const inputZoned = utcToZonedTime(date, timezone);
  const dayStartZoned = startOfDay(inputZoned);
  const dayStart = zonedTimeToUtc(dayStartZoned, timezone);
  return dayStart;
};

export const getEndOfDayInTimezone = (date: Date, timezone: string | 'local') => {
  if (timezone === 'local') return endOfDay(date);
  const inputZoned = utcToZonedTime(date, timezone);
  const dayStartZoned = endOfDay(inputZoned);
  const dayStart = zonedTimeToUtc(dayStartZoned, timezone);
  return dayStart;
};

export const getDateLocale = (locale: TimestampFormatOptions['locale'] = 'en') => locales[locale];

/**
 * Formats zoned timestamp to specified format:
 * - currently only supports en locale
 */
export const formatTimestamp = (
  timestamp: Date | string | number,
  {
    format: formatString = 'yyyy-MM-dd HH:mm:ss',
    timezone = LOCAL_TZ_VALUE,
    locale = 'en',
  }: Pick<TimestampFormatOptions, 'timezone' | 'locale'> & { format?: string } = {},
) => {
  const date = new Date(timestamp);

  const zonedTime = getZonedDate(date, getTimezoneValue(timezone)?.value);
  return format(zonedTime, formatString, {
    locale: getDateLocale(locale),
  });
};

/**
 * Formats timestamp to default format:
 *
 * Example outputs:
 * - Oct 26 · 15:27, for 24h format
 * - Oct 26 · 3:27 PM, for 12h format
 */
export const formatVerboseTimestamp = (
  timestamp: Date | string | number,
  {
    timezone = LOCAL_TZ_VALUE,
    timeFormat = '24',
    locale = 'en',
    dateFormat = 'MMM d',
    year = false,
    time = true,
    seconds = false,
  }: TimestampFormatOptions = {},
) => {
  const secondsFormatString = seconds ? ':ss' : '';
  const timeFormatString =
    timeFormat === '12' ? `h:mm${secondsFormatString} a` : `HH:mm${secondsFormatString}`;
  const dateTimeDivider = time && (dateFormat || year) ? ' · ' : '';
  const dateFormatString = `${dateFormat}${year ? ', yyyy' : ''}${dateTimeDivider}${
    time ? timeFormatString : ''
  }`;

  return formatTimestamp(timestamp, {
    format: dateFormatString,
    timezone,
    locale,
  });
};

const DURATION_MINUTE = 60;
const DURATION_HOUR = DURATION_MINUTE * 60;
const DURATION_DAY = DURATION_HOUR * 24;

const DURATION_SECONDS_SHORT_EN = "s 'sec.";
const DURATION_MINUTES_SHORT_EN = `m 'min. '${DURATION_SECONDS_SHORT_EN}`;
const DURATION_HOURS_SHORT_EN = `h 'hr. '${DURATION_MINUTES_SHORT_EN}`;
const DURATION_DAYS_SHORT_EN = `d 'dys. '${DURATION_HOURS_SHORT_EN}`;

const getDurationFormatter = (seconds: number) => {
  if (seconds < DURATION_MINUTE) return DURATION_SECONDS_SHORT_EN;
  if (seconds < DURATION_HOUR) return DURATION_MINUTES_SHORT_EN;
  if (seconds < DURATION_DAY) return DURATION_HOURS_SHORT_EN;
  return DURATION_DAYS_SHORT_EN;
};

/**
 * Formats duration to default format:
 * - currently only supports en locale
 *
 * Example outputs:
 * - 2 sec.
 * - 1 min. 2 sec.
 * - 2 hr. 1 min. 2sec.
 * - 1 dys. 2hr. 1 min. 2sec.
 */
export const formatDuration = (
  seconds: number,
  { locale = 'en' }: Pick<TimestampFormatOptions, 'locale'> = {},
) =>
  format(seconds * 1000, getDurationFormatter(seconds), {
    locale: getDateLocale(locale),
  });

/**
 * Get the earliest (next) run UTC date from an array of Schedules
 */
export const getNextScheduleRunDate = (schedules: Schedule[]): Date | undefined => {
  return schedules.reduce<Date | undefined>((curr, schedule) => {
    if (!schedule.nextEvent) {
      return curr;
    }

    const nextEvent = new Date(schedule.nextEvent.notBeforeTs);

    if (!curr) {
      return nextEvent;
    }

    return nextEvent.getTime() < curr.getTime() ? nextEvent : curr;
  }, undefined);
};
